import {
  BITCOIN_MIN_DECIMALS,
  DATABASE_DECIMALS_COUNT,
  FLOATING_POINT_REGEX,
} from "#/shared/constants"
import { FixedPointNumber } from "#/shared/types"

export const parseToFixedPointNumber = (
  floatingPointString: string,
): FixedPointNumber | undefined => {
  if (!floatingPointString.match(FLOATING_POINT_REGEX)) {
    return undefined
  }

  const [whole, decimals, ...extra] = floatingPointString.split(".")

  // Only one `.` supported.
  if (extra.length > 0) {
    return undefined
  }

  const noThousandsSeparatorWhole = whole.replace(",", "")
  const setDecimals = decimals ?? ""

  try {
    return {
      amount: BigInt([noThousandsSeparatorWhole, setDecimals].join("")),
      decimals: setDecimals.length,
    }
  } catch (error) {
    return undefined
  }
}

export const convertFixedPoint = (
  fixedPoint: bigint,
  fixedPointDecimals: number,
  targetDecimals: number,
): bigint => {
  if (fixedPointDecimals >= targetDecimals) {
    return fixedPoint / 10n ** BigInt(fixedPointDecimals - targetDecimals)
  }

  return fixedPoint * 10n ** BigInt(targetDecimals - fixedPointDecimals)
}

export const convertFixedPointNumber = (
  { amount, decimals }: FixedPointNumber,
  targetDecimals: number,
): FixedPointNumber => ({
  amount: convertFixedPoint(amount, decimals, targetDecimals),
  decimals: targetDecimals,
})

export const floatToBigInt = (
  amount: string | number,
  decimals = 18,
): bigint => {
  const parsedAmount = amount.toString()
  const fixedPointAmount = parseToFixedPointNumber(parsedAmount)

  if (typeof fixedPointAmount === "undefined") {
    return 0n
  }

  const decimalMatched = convertFixedPointNumber(fixedPointAmount, decimals)

  return decimalMatched.amount
}

export const bigIntToFloat = (
  amount: bigint,
  decimals = 18,
  desiredDecimals = BITCOIN_MIN_DECIMALS,
): string => {
  let isNegative = false
  let normalizedAmount = amount

  if (amount < 0n) {
    normalizedAmount = -amount
    isNegative = true
  }

  const desiredDecimalsAmount =
    normalizedAmount / 10n ** BigInt(Math.max(0, decimals - desiredDecimals))

  // Desired decimals amount with additional decimal to check if number
  // should be rounded up
  const desiredDecimalsAmountWithAdditionalDecimal =
    normalizedAmount /
    10n ** BigInt(Math.max(0, decimals - desiredDecimals - 1))

  // We are getting the first value that has been cut off
  const firstDecimalAfterCutOff = desiredDecimalsAmountWithAdditionalDecimal
    .toString()
    .at(-1)

  // Padding amount when its length is smaller than desired
  const parsedDesiredAmount = desiredDecimalsAmount
    .toString()
    .padStart(desiredDecimals + 1, "0")

  const integersPart = parsedDesiredAmount.slice(0, -desiredDecimals)
  const decimalsPart = parsedDesiredAmount.slice(-desiredDecimals)

  // If number is above or equal to 5 we should round up the number
  const shouldIncreaseDecimalsPart =
    Number(firstDecimalAfterCutOff) >= 5 && desiredDecimals !== decimals

  // If above is true we add 1 to the decimals value and convert it back to string
  const adjustedDecimalsPart = shouldIncreaseDecimalsPart
    ? (Number(decimalsPart) + 1).toString().padStart(desiredDecimals, "0")
    : decimalsPart

  // Determining whether integer part needs to be increased due to rounding up
  const shouldIncreaseIntegerPart =
    (decimalsPart === "0" && adjustedDecimalsPart !== decimalsPart) ||
    adjustedDecimalsPart.length > decimalsPart.length

  // If above is true we add 1 to the integers value and convert it back to string
  // and set first value of decimals part to 0
  const adjustedIntegersPart = shouldIncreaseIntegerPart
    ? (Number(integersPart) + 1).toString()
    : integersPart

  const decimalsPartAfterIntegersAdjustments = shouldIncreaseIntegerPart
    ? `0${adjustedDecimalsPart.slice(1)}`
    : adjustedDecimalsPart

  // Inserting "." to separate integers part from decimal part
  const desiredAmount = [
    ...adjustedIntegersPart,
    ".",
    ...decimalsPartAfterIntegersAdjustments,
  ].join("")

  // Remove trailing zeros and the trailing decimal point (if exists)
  const trimmedDesiredAmount = desiredAmount.replace(
    /(?:\.0*|(\.\d+?)0+)$/,
    "$1",
  )

  return isNegative ? `-${trimmedDesiredAmount}` : trimmedDesiredAmount
}

// NOTE: This function should only be used in JSX code to render a human-friendly value.
// Do not use it in any calculations and the value is being formatted when it is too low
export const bigIntToHumanReadableFormat = (
  amount: bigint,
  decimals = 18,
  desiredDecimals = BITCOIN_MIN_DECIMALS,
): string => {
  let isNegative = false
  let normalizedAmount = amount

  if (amount < 0n) {
    normalizedAmount = -amount
    isNegative = true
  }

  const trimmedDesiredAmount = bigIntToFloat(
    normalizedAmount,
    decimals,
    desiredDecimals,
  )

  // If value is very small we want to present it in a nice way
  if (trimmedDesiredAmount === "0" && amount !== 0n) {
    // Pad start will help us with precision issues
    const decimalsString = "1".padStart(desiredDecimals, "0")
    return `<0.${decimalsString}`
  }

  return isNegative ? `-${trimmedDesiredAmount}` : trimmedDesiredAmount
}

// Convert bigint amount with 18 decimals to token decimals format
// We need to divide the amount by difference of database decimals and token decimals
export const convertDecimalsToTokenDecimals = (
  amount: bigint,
  tokenDecimals: number,
): bigint => amount / 10n ** BigInt(DATABASE_DECIMALS_COUNT - tokenDecimals)

export const separateThousandsWithComma = (
  value: number | bigint | string,
  minDecimals = 2,
  maxDecimals = minDecimals,
): string => {
  // If passed value is not numeric we should return 0
  if (Number.isNaN(+value.toString())) return "0"

  const adjustedValue = typeof value === "string" ? +value : value
  // @ts-expect-error - `maximumFractionDigits` wants to get number less than 21,
  // but as the tokens have 18 decimals have, we can safely pass a parameter
  return adjustedValue.toLocaleString("en-US", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals < 21 ? maxDecimals : 2,
    roundingMode: "halfEven",
  })
}

// separateThousandsWithComma() function changes the string value to number,
// which results in strange behavior for value bigger than 999... (x16).
// For now we will just use regex for that.
export const separateThousandsWithComaRegExp = (value: string) =>
  value.replace(/\B(?=(\d{3})+(?!\d))/g, ",")

export const bigIntToCommaSeparatedFloat = (
  value: bigint,
  floatDecimals = 18,
  floatDesiredDecimals = BITCOIN_MIN_DECIMALS,
  finalMinDecimals = 2,
  finalMaxDecimals = finalMinDecimals,
) =>
  separateThousandsWithComma(
    bigIntToFloat(value, floatDecimals, floatDesiredDecimals),
    finalMinDecimals,
    finalMaxDecimals,
  )

export const convertBigNumberShortFormat = (
  value: string | number,
): { shortFormat: string; converted: boolean } => {
  let converted = false
  let formattedValue = value

  const trillion = 1_000_000_000_000
  const bilion = 1_000_000_000
  const milion = 1_000_000

  const parsedValue = Number(value)

  if (parsedValue >= trillion) {
    formattedValue = `${(parsedValue / trillion).toFixed(1)}T`
    converted = true
  }

  if (parsedValue >= bilion) {
    formattedValue = `${(parsedValue / bilion).toFixed(1)}B`
    converted = true
  }

  if (parsedValue >= milion) {
    formattedValue = `${(parsedValue / milion).toFixed(1)}M`
    converted = true
  }

  return { shortFormat: formattedValue.toString(), converted }
}

// This function format in the following format
// 0.53523 -> 0.53
// 10.2343 -> 10.2
// 100.353 -> 100
export const formatNumberToShortFormat = (value: string | number): string => {
  const parsedValue = Number(value)

  if (parsedValue >= 100) {
    return Math.floor(parsedValue).toString()
  }

  if (parsedValue >= 10) {
    return parsedValue.toFixed(1)
  }

  return parsedValue.toFixed(2)
}

export const convertToNonExponentional = (value: number): string => {
  const stringValue = value.toString()

  if (!stringValue.includes("e")) return value.toString()

  if (Math.abs(value) < 1) {
    const nonExponentionalPart = Number(stringValue.split("e-")[0])
    const exponentionalPart = Number(stringValue.split("e-")[1])

    if (!exponentionalPart) return stringValue

    return `0.${"0".repeat(exponentionalPart - 1)}${nonExponentionalPart}`
  }

  const exponentionalPart = Number(stringValue.split("e+")[1])

  if (exponentionalPart <= 20) return stringValue

  const adjustedExponentionalPart = exponentionalPart - 20
  const missingDecimals = 10 ** adjustedExponentionalPart
  const dividedValue = value / missingDecimals

  return dividedValue.toString() + "0".repeat(adjustedExponentionalPart)
}

export const operateOnDecimalPointValues = (
  a: number,
  b: number,
  operation: "+" | "-" = "+",
  decimals: number = 18,
) => {
  // We need to convert to non-exponentional value so f.e. 1e-7 is processed correctly
  const firstConvertedValue = convertToNonExponentional(a)
  const secondConvertedValue = convertToNonExponentional(b)

  // Convert to BigInt with required decimals amount
  const bigIntFirstValue = floatToBigInt(firstConvertedValue, decimals)

  const bigIntSecondValue = floatToBigInt(secondConvertedValue, decimals)

  let result = 0n

  switch (operation) {
    case "-":
      result = bigIntFirstValue - bigIntSecondValue
      break
    default:
      result = bigIntFirstValue + bigIntSecondValue
  }

  // Convert back to human readable format
  return Number(bigIntToFloat(result, decimals, decimals))
}

/**
 * This function is used to multiply bigint value with decimal point value in
 * order to avoid any precision issues. We are taking decimals count which will
 * be used a scale to get rid of decimal places in provided number.
 * If no decimals are present, we are proceeding with normal calculation, else
 * we need to scale the number and add the end divide by the scale to get
 * correct result
 */
export const multiplyBigIntWithDecimal = (
  bigint: bigint,
  number: number,
): bigint => {
  const numberString = number.toString()
  const scientificMatch = /^1e-(\d+)$/.exec(numberString)

  // Ensure we can correctly deal with scientific number expression
  if (scientificMatch) {
    const exponent = parseInt(scientificMatch[1], 10)
    const scale = BigInt(10 ** exponent)

    return (bigint * 1n) / scale
  }

  const decimalsPart = number.toString().split(".")[1]
  const decimalsCount = decimalsPart ? decimalsPart.length : 0

  if (!decimalsCount) return bigint * BigInt(number)

  const scale = BigInt(10 ** decimalsCount)
  const scaledNumber = BigInt(Math.round(number * Number(scale)))

  return (bigint * scaledNumber) / scale
}

export const trimDecimals = (value: string, precision: number): string => {
  const [integerPart, decimalPart = ""] = value.split(".")
  return decimalPart.length > precision
    ? `${integerPart}.${decimalPart.slice(0, precision)}`
    : value
}

export const trimBeforeDecimals = (
  value: string,
  precision: number,
): string => {
  const [integerPart, decimalPart = ""] = value.split(".")

  return integerPart.length > precision
    ? `${integerPart.slice(0, precision)}.${decimalPart}`
    : value
}

// Normalize number input to allow valid decimal numbers and prevent multiple dots
export const normalizeDecimalNumberInput = (value: string) =>
  value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
